/**
 * "Baxter's magic" — the New Site loader (concept 07 of the loader storyboards,
 * .local/.output/20261103-baxter-loader-storyboards). Pure parts only: the
 * overlay (BaxterMagic.tsx) renders them from the panel's live SSE events.
 */

/** The panel re-broadcasts every SSE event of a turn on this window event. */
export const BAXTER_STREAM_EVENT = 'wpp-baxter-stream';
export interface BaxterStreamDetail {
  siteSlug: string;
  /** An SSE event name, or `end` when the turn's stream closes. */
  event: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}
export function emitBaxterStream(siteSlug: string, event: string, data: unknown) {
  window.dispatchEvent(new CustomEvent<BaxterStreamDetail>(BAXTER_STREAM_EVENT, { detail: { siteSlug, event, data } }));
}

/** The wizard asks for the loader; the site shell shows it once. */
const magicKey = (siteSlug: string) => `wpp-baxter-magic:${siteSlug}`;
export interface MagicRequest {
  /** The site's name, for the title until Baxter names it. */
  name?: string;
  /** What the person said about colours, photos or fonts, if anything. */
  style?: string;
}
export function requestMagic(siteSlug: string, request: MagicRequest) {
  try { sessionStorage.setItem(magicKey(siteSlug), JSON.stringify(request)); } catch { /* no loader without storage */ }
}
export function peekMagic(siteSlug: string): MagicRequest | null {
  try {
    const raw = sessionStorage.getItem(magicKey(siteSlug));
    return raw ? (JSON.parse(raw) as MagicRequest) : null;
  } catch { return null; }
}
export function clearMagic(siteSlug: string) {
  try { sessionStorage.removeItem(magicKey(siteSlug)); } catch { /* nothing stored */ }
}

export const PHASES = [
  { key: 'read', label: 'Reading your answers', tools: ['wpp_get_settings', 'wpp_list_forms', 'wpp_update_settings', 'wpp_digest_url', 'wpp_fetch_url', 'wpp_render_url'] },
  { key: 'photos', label: 'Bringing photos', tools: ['wpp_import_media', 'wpp_resize_media'] },
  { key: 'theme', label: 'Writing the theme', tools: ['wpp_fork_theme', 'wpp_read_theme_file', 'wpp_write_theme_file', 'wpp_get_theme_files'] },
  { key: 'pages', label: 'Writing pages', tools: ['wpp_create_form', 'wpp_update_form', 'wpp_create_content', 'wpp_update_content', 'wpp_list_content'] },
  { key: 'build', label: 'Building preview', tools: ['wpp_build_site', 'wpp_create_preview_share'] },
] as const;

/** Index into PHASES, or -1 for a tool that does not move the rail. */
export const phaseOf = (name: string) => PHASES.findIndex((p) => (p.tools as readonly string[]).includes(name));

/** The tools whose success makes something the site gains (a card flies for these). */
export const MAKES = new Set(['wpp_import_media', 'wpp_write_theme_file', 'wpp_create_content']);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Args = Record<string, any>;

/** One short, human line per call — the hint of the current step. Failures read as "Adjusting", never red. */
export function hintOf(name: string, a: Args = {}, ok = true): string {
  const title = typeof a.title === 'string' && a.title ? `“${a.title.length > 38 ? `${a.title.slice(0, 36)}…` : a.title}”` : '';
  if (!ok) return `Adjusting ${title || 'that step'}`;
  switch (name) {
    case 'wpp_get_settings': return 'Checking your settings';
    case 'wpp_list_forms': return 'Looking for existing forms';
    case 'wpp_update_settings': return a.favicon_url ? 'Setting your tab icon' : `Naming it ${title || 'your site'}`;
    case 'wpp_digest_url': case 'wpp_fetch_url': case 'wpp_render_url': return 'Reading your current site';
    case 'wpp_import_media': return a.query ? `Finding photos: “${a.query}”` : 'Bringing your photos';
    case 'wpp_resize_media': return 'Sizing a photo';
    case 'wpp_fork_theme': return 'Copying the starter theme';
    case 'wpp_get_theme_files': return 'Looking over the theme';
    case 'wpp_read_theme_file': return `Reading ${a.path ?? 'a theme file'}`;
    case 'wpp_write_theme_file': return `Writing ${a.path ?? 'a theme file'}`;
    case 'wpp_create_form': case 'wpp_update_form': return `Adding the ${a.title ?? a.name ?? 'contact'} form`;
    case 'wpp_create_content': return `Writing ${title || 'a page'}`;
    case 'wpp_update_content': return a.menu_order != null ? 'Ordering the menu' : a.feature_image ? 'Adding a cover photo' : 'Polishing a page';
    case 'wpp_list_content': return 'Checking every page';
    case 'wpp_build_site': return 'Building the site';
    case 'wpp_create_preview_share': return 'Making your private link';
    default: return 'Working';
  }
}

/** The colours Baxter wrote, named: each `--name: #hex` in the `:root` block of static/style.css (max 8, unique hex). */
export function paletteVars(css: string): Array<{ name: string; hex: string }> {
  const root = /:root\s*\{([^}]*)\}/.exec(css)?.[1] ?? '';
  const out: Array<{ name: string; hex: string }> = [];
  for (const [, name, hex] of root.matchAll(/--([\w-]+)\s*:\s*(#(?:[0-9a-f]{6}|[0-9a-f]{3}))\b/gi)) {
    const h = hex.toLowerCase();
    if (!out.some((v) => v.hex === h)) out.push({ name, hex: h });
  }
  return out.slice(0, 8);
}

/** Just the hex values of `paletteVars`. */
export const paletteOf = (css: string) => paletteVars(css).map((v) => v.hex);

/** Small deterministic PRNG (mulberry32) so a seed always lays the same sky. */
export function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export interface Star { x: number; y: number; r: number; o: number; twinkle: number | null }

/**
 * A sky that covers the whole loader without clumps: one jittered point per
 * grid cell (a cheap blue-noise), some cells left empty, sizes skewed small.
 * x/y are percentages; `twinkle` is an animation delay in seconds or null.
 */
export function starfield(seed: number, cols = 18, rows = 12): Star[] {
  const rand = rng(seed);
  const stars: Star[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (rand() < 0.3) continue;
      const big = rand() < 0.08;
      stars.push({
        x: ((col + 0.15 + rand() * 0.7) / cols) * 100,
        y: ((row + 0.15 + rand() * 0.7) / rows) * 100,
        r: big ? 1.1 + rand() * 0.6 : 0.35 + rand() * 0.55,
        o: big ? 0.7 + rand() * 0.3 : 0.2 + rand() * 0.45,
        twinkle: rand() < 0.22 ? Math.round(rand() * 60) / 10 : null,
      });
    }
  }
  return stars;
}
